import { useCallback, useMemo, useState } from 'react';
import { useSocketSearch } from 'src/utils/socket/useSocketSearch';
import { SocketContextSentinel } from 'src/utils/socket/context/sentinelSocketContext';
import { selectBleBundleFiles, selectBleConnectedDevice, selectBleRemoteLogFiles } from 'src/redux/ble/selectors';
import { useSelector } from 'react-redux';
import ConnectedControllerDetails from './ConnectedControllerDetails';
import { Firmware, SearchFirmwareRequest } from 'src/models/firmware';
import { compareFirmwareVersions } from 'src/utils/helpers/firmwareHelpers';
import { useHistory } from 'react-router';
import _ from 'lodash';
import { UpdateFirmwareModal } from 'src/pages/mobile-controller-page/UpdateFirmwareModal';
import { BleService } from 'src/ble/BleService';
import { useIntl } from 'react-intl';
import { GenericMessages } from 'src/intl/GenericMessages';
import { ControllerMessages } from 'src/intl/ControllerMessages';
import { selectLogLocalDataByFilename } from 'src/redux/log/selectors';
import { getFirmwareVersion } from 'src/utils/helpers/referenceHelpers';
import { useSocketList } from 'src/utils/socket';
import { Controller } from 'src/models/controller';

export interface FirmwareInfo {
  mostRecentFirmware: Firmware;
  isFirmwareOutdated: boolean;
}

const ConnectedControllerCard: React.FC = () => {
  const history = useHistory();
  const { formatMessage } = useIntl();
  const bundleFiles = useSelector(selectBleBundleFiles);
  const connectedDevice = useSelector(selectBleConnectedDevice);
  const controllerLogFiles = useSelector(selectBleRemoteLogFiles);
  const localLogFiles = useSelector(selectLogLocalDataByFilename);
  const [updateFirmwareModalOpen, setUpdateFirmwareModalOpen] = useState(false);

  // Controllers
  const controllerQuery = useMemo(() => {
    return { query: { ssn: connectedDevice?.serialNumber } };
  }, [connectedDevice?.serialNumber]);

  const { items: controllers }: { items: Controller[] } = useSocketList(
    SocketContextSentinel,
    'controllers',
    controllerQuery,
  );

  // Jobs
  const latestLiveJob = useMemo(() => {
    const localLogFilesList = Object.values(localLogFiles);
    if (localLogFilesList.length === 0) return;

    const liveJobs = localLogFilesList.filter(log => log.pendingClose === false);
    const latestLiveJobFilename = _.last(liveJobs)?.filename;

    return latestLiveJobFilename?.replace('.log', '');
  }, [localLogFiles]);

  // Firmware
  const [fwSearchRequest] = useState<SearchFirmwareRequest>({
    hidden: 'false',
    page: 0,
    sortOrder: 'desc',
    pageSize: 10000,
    sortProperty: 'createdAt',
  });

  const { items: firmwareSearchResult } = useSocketSearch(SocketContextSentinel, 'firmware', fwSearchRequest);
  const registeredController = connectedDevice ? _.first(controllers) : undefined;

  /** The latest version of firmware from the API. */
  const firmwareInfo = useMemo((): FirmwareInfo | undefined => {
    const connectedControllerFirmwareVersion = () => {
      if (!registeredController) {
        return undefined;
      }

      if (registeredController.fw) {
        return getFirmwareVersion(registeredController.fw, firmwareSearchResult?.results);
      }

      return connectedDevice?.firmwareVersion;
    };
    if (!firmwareSearchResult?.results?.length) {
      return undefined;
    }

    // Filter out dev versions of firmware

    if (!connectedControllerFirmwareVersion) {
      return;
    }

    // Filter out down to global versions for comparison purposes
    const globalFirmware = firmwareSearchResult.results.filter(item => item && item.releaseType === 'global');

    // Sort based on semantic version #
    const sortedFirmware = globalFirmware.sort((a, b) => compareFirmwareVersions(b.version, a.version));

    const isFirmwareOutdated =
      compareFirmwareVersions(connectedControllerFirmwareVersion(), sortedFirmware[0].version) < 0; // connectedController.fw

    // Return latest firmware
    return { mostRecentFirmware: sortedFirmware[0], isFirmwareOutdated };
  }, [connectedDevice?.firmwareVersion, firmwareSearchResult.results, registeredController]);

  const handleUpdateFirmware = useCallback((e: React.MouseEvent<HTMLIonButtonElement, MouseEvent>) => {
    e.stopPropagation();
    setUpdateFirmwareModalOpen(true);
  }, []);

  /**
   * Updates the firmware of the connected controller
   */
  const updateFirmwareVersion = useCallback(async (firmware: Firmware | undefined) => {
    if (BleService.controller?.updateFirmware && firmware) {
      try {
        await BleService.controller.updateFirmware(firmware);

        setUpdateFirmwareModalOpen(false);
      } catch (err) {
        console.error('Error updating firmware');
      }
    } else {
      console.error('Could not update firmware!', firmware);
    }
  }, []);

  /**
   * The text to display for the firmware version and whether an update is available
   */
  const firmwareText = useMemo(() => {
    const version = connectedDevice?.firmwareVersion;
    const isOutdated = firmwareInfo?.isFirmwareOutdated;

    if (version) {
      const updateMessage = ` - ${
        isOutdated ? formatMessage(ControllerMessages.updateAvailable) : formatMessage(ControllerMessages.latest)
      }`;

      return `${version}${updateMessage}`;
    }

    return formatMessage(GenericMessages.unknownPlaceholder);
  }, [connectedDevice?.firmwareVersion, firmwareInfo?.isFirmwareOutdated, formatMessage]);

  const physicalSerial = useMemo(() => {
    if (connectedDevice?.physicalSerial) {
      return connectedDevice.physicalSerial;
    }
    return formatMessage(GenericMessages.unknownPlaceholder);
  }, [connectedDevice?.physicalSerial, formatMessage]);

  return (
    <>
      <ConnectedControllerDetails
        physicalSerial={registeredController?.physicalSerial ?? physicalSerial}
        liveJob={latestLiveJob}
        firmware={firmwareText}
        jobCount={controllerLogFiles.length}
        bundleCount={bundleFiles.length}
        onUpdateFirmware={firmwareInfo?.isFirmwareOutdated ? handleUpdateFirmware : undefined}
        onCardClick={() => history.push(`/controllers/${connectedDevice?.serialNumber}`)}
      />
      <UpdateFirmwareModal
        onSave={updateFirmwareVersion}
        isOpen={updateFirmwareModalOpen}
        firmwares={firmwareSearchResult?.results}
        onClose={() => setUpdateFirmwareModalOpen(false)}
        mostRecentVersion={firmwareInfo?.mostRecentFirmware}
      />
    </>
  );
};

export default ConnectedControllerCard;
